import { City, Country, State } from 'country-state-city';

const AFRICA_COUNTRY_CODES = [
  'DZ',
  'AO',
  'BJ',
  'BW',
  'BF',
  'BI',
  'CV',
  'CM',
  'CF',
  'TD',
  'KM',
  'CD',
  'CG',
  'CI',
  'DJ',
  'EG',
  'GQ',
  'ER',
  'SZ',
  'ET',
  'GA',
  'GM',
  'GH',
  'GN',
  'GW',
  'KE',
  'LS',
  'LR',
  'LY',
  'MG',
  'MW',
  'ML',
  'MR',
  'MU',
  'MA',
  'MZ',
  'NA',
  'NE',
  'NG',
  'RW',
  'ST',
  'SN',
  'SC',
  'SL',
  'SO',
  'ZA',
  'SS',
  'SD',
  'TZ',
  'TG',
  'TN',
  'UG',
  'ZM',
  'ZW',
  'EH',
];

const EUROPE_COUNTRY_CODES = [
  'AL',
  'AD',
  'AT',
  'BY',
  'BE',
  'BA',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IS',
  'IE',
  'IT',
  'XK',
  'LV',
  'LI',
  'LT',
  'LU',
  'MT',
  'MD',
  'MC',
  'ME',
  'NL',
  'MK',
  'NO',
  'PL',
  'PT',
  'RO',
  'RU',
  'SM',
  'RS',
  'SK',
  'SI',
  'ES',
  'SE',
  'CH',
  'TR',
  'UA',
  'GB',
  'VA',
];

const AFRICA_COUNTRIES = Country.getAllCountries()
  .filter((country) => AFRICA_COUNTRY_CODES.includes(country.isoCode))
  .sort((a, b) => a.name.localeCompare(b.name));

const EUROPE_COUNTRIES = Country.getAllCountries()
  .filter((country) => EUROPE_COUNTRY_CODES.includes(country.isoCode))
  .sort((a, b) => a.name.localeCompare(b.name));

const US_STATES = State.getStatesOfCountry('US').sort((a, b) => a.name.localeCompare(b.name));
const AU_STATES = State.getStatesOfCountry('AU').sort((a, b) => a.name.localeCompare(b.name));
const CA_STATES = State.getStatesOfCountry('CA').sort((a, b) => a.name.localeCompare(b.name));

const REGION_CONFIG = {
  us: { basePath: '', label: 'USA', stateLabel: 'State', stateLabelPlural: 'States' },
  af: { basePath: '/af', label: 'Africa', stateLabel: 'State', stateLabelPlural: 'States' },
  au: { basePath: '/au', label: 'Australia', stateLabel: 'State', stateLabelPlural: 'States' },
  ca: { basePath: '/ca', label: 'Canada', stateLabel: 'Province', stateLabelPlural: 'Provinces' },
  eu: { basePath: '/eu', label: 'Europe', stateLabel: 'Country', stateLabelPlural: 'Countries' },
};

const isRegionPath = (pathname = '', code) =>
  pathname === `/${code}` || pathname.startsWith(`/${code}/`);

export const getRegionFromPathname = (pathname = '') => {
  if (isRegionPath(pathname, 'af')) return 'af';
  if (isRegionPath(pathname, 'au')) return 'au';
  if (isRegionPath(pathname, 'ca')) return 'ca';
  if (isRegionPath(pathname, 'eu')) return 'eu';
  return 'us';
};

export const getBasePathForRegion = (region) => REGION_CONFIG[region]?.basePath || '';

export const getRegionOptions = () => [
  { value: 'us', label: 'United States' },
  { value: 'af', label: 'Africa' },
  { value: 'au', label: 'Australia' },
  { value: 'ca', label: 'Canada' },
  { value: 'eu', label: 'Europe' },
];

export const getRegionContextFromPathname = (pathname = '') => {
  const region = getRegionFromPathname(pathname);
  return {
    region,
    basePath: getBasePathForRegion(region),
  };
};

export const getRegionStateLabel = (region) => REGION_CONFIG[region]?.stateLabel || 'State';

export const getRegionStateLabelPlural = (region) =>
  REGION_CONFIG[region]?.stateLabelPlural || 'States';

export const getRegionLocationLabel = (region) => REGION_CONFIG[region]?.label || 'USA';

export const getRegionCountryOptions = (region) => {
  if (region === 'af') return AFRICA_COUNTRIES.map((item) => item.name);
  if (region === 'eu') return EUROPE_COUNTRIES.map((item) => item.name);
  return [];
};

export const getRegionCountryRecord = (region, countryName) => {
  const list = region === 'af' ? AFRICA_COUNTRIES : region === 'eu' ? EUROPE_COUNTRIES : [];
  return list.find(
    (item) => item.name.trim().toLowerCase() === String(countryName || '').trim().toLowerCase()
  );
};

export const getRegionStateOptions = (region, countryRecord) => {
  if (region === 'af') {
    if (!countryRecord?.isoCode) return [];
    return State.getStatesOfCountry(countryRecord.isoCode).map((item) => item.name);
  }
  if (region === 'au') return AU_STATES.map((item) => item.name);
  if (region === 'ca') return CA_STATES.map((item) => item.name);
  if (region === 'eu') return EUROPE_COUNTRIES.map((item) => item.name);
  return US_STATES.map((item) => item.name);
};

export const getRegionStateRecord = (region, stateName, countryRecord) => {
  if (region === 'af') {
    if (!countryRecord?.isoCode) return null;
    const states = State.getStatesOfCountry(countryRecord.isoCode);
    return states.find(
      (item) => item.name.trim().toLowerCase() === String(stateName || '').trim().toLowerCase()
    );
  }
  if (region === 'au') {
    return AU_STATES.find(
      (item) => item.name.trim().toLowerCase() === String(stateName || '').trim().toLowerCase()
    );
  }
  if (region === 'ca') {
    return CA_STATES.find(
      (item) => item.name.trim().toLowerCase() === String(stateName || '').trim().toLowerCase()
    );
  }
  if (region === 'eu') {
    return getRegionCountryRecord('eu', stateName);
  }
  return US_STATES.find(
    (item) => item.name.trim().toLowerCase() === String(stateName || '').trim().toLowerCase()
  );
};

export const getRegionCityOptions = (region, stateRecord, countryRecord) => {
  if (region === 'af') {
    if (!stateRecord?.isoCode || !countryRecord?.isoCode) return [];
    return City.getCitiesOfState(countryRecord.isoCode, stateRecord.isoCode).map((city) => city.name);
  }
  if (region === 'eu') {
    if (!stateRecord?.isoCode) return [];
    return City.getCitiesOfCountry(stateRecord.isoCode).map((city) => city.name);
  }
  if (!stateRecord?.isoCode) return [];
  if (region === 'au') {
    return City.getCitiesOfState('AU', stateRecord.isoCode).map((city) => city.name);
  }
  if (region === 'ca') {
    return City.getCitiesOfState('CA', stateRecord.isoCode).map((city) => city.name);
  }
  return City.getCitiesOfState('US', stateRecord.isoCode).map((city) => city.name);
};

export const getRegionFlagUrl = (region, stateRecord) => {
  const code = stateRecord?.isoCode;
  if (region === 'us') {
    if (!code) return '';
    return `https://flagcdn.com/us-${code.toLowerCase()}.svg`;
  }
  if (region === 'au') {
    return 'https://flagcdn.com/au.svg';
  }
  if (region === 'ca') {
    return 'https://flagcdn.com/ca.svg';
  }
  if (region === 'eu') {
    return 'https://flagcdn.com/eu.svg';
  }
  if (!code) return '';
  return `https://flagcdn.com/${code.toLowerCase()}.svg`;
};

export const getRegionFlagUrlForRegion = (region) => {
  if (region === 'us') return 'https://flagcdn.com/us.svg';
  if (region === 'au') return 'https://flagcdn.com/au.svg';
  if (region === 'ca') return 'https://flagcdn.com/ca.svg';
  if (region === 'eu') return 'https://flagcdn.com/eu.svg';
  return '';
};
